import Vue from 'vue'
import Vuex from 'vuex'
import { getMiles, getMPH, getHHMMSS, getFeet } from '@/helpers/conversion'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
const moment = extendMoment(Moment)

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    access_token: undefined,
    activitiesOverTime: [],
    activitiesRaw: [],
    athlete: [],
    baseURL: 'https://www.strava.com/api/v3',
    dataLoading: true,
    expires_at: undefined,
    extremes: {
      min: moment().subtract(3, 'months').unix(Number) * 1000,
      max: new Date().getTime()
    },
    hideRestDays: false,
    loadingStatus: 'Fetching Strava data…',
    oAuthBaseURL: 'https://www.strava.com/oauth/token',
    refresh_token: undefined,
    selectedRide: {},
  },
  getters: {
    getActivityCount: state => state.activitiesRaw.length,
    getActivitiesOverTime: state => state.activitiesOverTime,
    getAverageDistance(state) {
      const distances = state.activitiesRaw.map(item => item.distance)
      return Math.round(distances.reduce((a, b) => a + b, 0) / distances.length)
    },
    getDateRange(state, getters) {
      const start = getters.getStravaStartDate,
            end = moment()
      
      return moment.range(start, end)
    },
    getExtremes: state => state.extremes,
    getExtremesMin: state => state.extremes.min,
    getExtremesMax: state => state.extremes.max,
    getStravaStartDate: state => state.activitiesRaw[state.activitiesRaw.length - 1].start_date,
    getMaxSpeed: state => getMPH(Math.max(...state.activitiesRaw.map(item => item.max_speed))),
    getMaxWattage(state) {
      return Math.max(...state.activitiesRaw
        .map(item => item.max_watts)
        .filter(item => item != null))
    },
    getMaxDistance: state => Math.max(...state.activitiesRaw.map(item => item.distance))
  },
  mutations: {
    setStravaTokens(state, data) {
      for (const property in data) {
        state[property] = data[property]
        if (property == 'athlete') {
          localStorage.setItem(property, JSON.stringify(data[property]))
        } else {
          localStorage.setItem(property, data[property])
        }
      }
    },
    setActivitiesRaw: (state, data) => state.activitiesRaw = data,
    setActivitiesOverTime: (state, data) => state.activitiesOverTime = data,
    setLoadingState: (state, boolean) => state.dataLoading = boolean,
    setLoadingStatus: (state, loadingStatus) => state.loadingStatus = loadingStatus,
    setSelectedRide: (state, ride) => state.selectedRide = ride,
    setExtremes: (state, extremes) => state.extremes = extremes
  },
  actions: {
    async fetchStravaAuth({ commit, state }, authCode) {
      const params = {
        client_id: process.env.VUE_APP_CLIENT_ID,
        client_secret: process.env.VUE_APP_CLIENT_SECRET,
        code: authCode,
        grant_type: 'authorization_code'
      }
      const url = new URL(state.oAuthBaseURL)
      url.search = new URLSearchParams(params)
      await fetch(url, {
        method: 'POST'
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok')
          } else {
            return response.json()
          }
        })
        .then(data => {
          commit('setStravaTokens', data)
          console.log(`succesfully got the oauth stuff:
          expires: ${data.expires_at}
          refresh token: ${data.refresh_token}
          access token: ${data.access_token}
          `)
        })
        .catch(error => {
          console.error('There has been a problem with your fetch operation:', error)
        })
        // sample response
        // {
        //   "token_type": "Bearer",
        //   "expires_at": 1568775134,
        //   "expires_in": 21600,
        //   "refresh_token": "e5n567567...",
        //   "access_token": "a4b945687g...",
        //   "athlete": {
        //     #{ summary athlete representation }
        //   }
        // }
    },
    async getStravaRefreshedToken({ commit, state, dispatch }, refresh_token) {
      console.log('getting new token (getStravaRefreshedToken)')
      // get new token, set it in store, then run getStravaActivities again
      const params = {
        client_id: process.env.VUE_APP_CLIENT_ID,
        client_secret: process.env.VUE_APP_CLIENT_SECRET,
        grant_type: 'refresh_token',
        refresh_token: refresh_token,
      }
      const url = new URL(state.oAuthBaseURL)
      url.search = new URLSearchParams(params)
      await fetch(url, {
        method: 'POST'
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok')
          } else {
            return response.json()
          }
        })
        .then(async data => {
          commit('setStravaTokens', data)
          console.log(`succesfully got refresh stuff:
          expires: ${data.expires_at}
          refresh token: ${data.refresh_token}
          access token: ${data.access_token}
          `)
          await dispatch('getStravaActivities')
          
        })
        .catch(error => {
          console.error('There has been a problem with your fetch operation:', error)
        })
        // sample response
        // {
        //   "token_type": "Bearer",
        //   "access_token": "a9b723...",
        //   "expires_at": 1568775134,
        //   "expires_in": 20566,
        //   "refresh_token": "b5c569..."
        // }
    },
    getStravaActivities({ commit, dispatch, state }) {
      console.log('fetching new data (getStravaActivities)')
      
      const getData = async function(page = 1) {
        const params = {
          access_token: state.access_token,
          page: page,
          per_page: 200,
          scope: 'activity:read_all'
        }
        const url = new URL(state.baseURL + '/athlete/activities')
        url.search = new URLSearchParams(params)
        const response = await fetch(url)
        if (response.ok) return response.json()
      }

      async function fetchStravaActivities(page = 1) {
        const results = await getData(page)
        
        if (results.length > 0) {
          return results.concat(await fetchStravaActivities(page+1))
        } else {
          return results
        }
      }

      (async () => {
        const entireList = await fetchStravaActivities()
        commit('setActivitiesRaw', entireList)
        localStorage.setItem('activitiesRaw', JSON.stringify(entireList))
        await dispatch('prepareActivities', entireList)
        commit('setSelectedRide', entireList[0])
        await dispatch('updateLoadingState', false)
      })()
    },
    prepareActivities({ commit, getters }, activities) {
      const activitiesOverTime = [],
            keys = Object.keys(activities[0]),
            rangeArray = Array.from(getters.getDateRange.by('days'))
      
      // for (let day of getters.getDateRange.by('days')) {
      //   rangeArray.push(moment(day))
      // }

      rangeArray.forEach(day => {
        const matchedDay = activities.find(element => day.isSame(element.start_date, 'day'))
        if (matchedDay != undefined) {

          activitiesOverTime.push(matchedDay)
          // activitiesOverTime.push({
          //   ...matchedDay,
          //   distance: getMiles(matchedDay.distance),
          //   total_elevation_gain: getFeet(matchedDay.total_elevation_gain),
          //   average_speed: getMPH(matchedDay.average_speed),
          //   max_speed: getMPH(matchedDay.max_speed)
      // this introduces bugs in the highchart. I need to separate multi-day info into a new series and stack it
      // getters.getDateRange.forEach(day => {
      //   const matchedDays = activities.filter(element => day.isSame(element.start_date, 'day'))
      //   if (matchedDays.length > 0) {
      //     matchedDays.forEach(day => {
      //       activitiesOverTime.push({
      //         ...day,
      //         distance: getMiles(day.distance),
      //         total_elevation_gain: getFeet(day.total_elevation_gain),
      //         average_speed: getMPH(day.average_speed),
      //         max_speed: getMPH(day.max_speed)
      //       })
          // })
        } else {
          const newObject = {}
          keys.forEach(key => {
            newObject[key] = null
          })
          activitiesOverTime.push({
            ...newObject,
            start_date: moment.utc(day).format(),
            // distance: 0
          })
        }
      })
      commit('setActivitiesOverTime', activitiesOverTime)
      localStorage.setItem('activitiesOverTime', JSON.stringify(activitiesOverTime))
    },
    async refreshStravaData({ dispatch }) {
      console.log('refreshing strava data')
      dispatch('updateLoadingState', true)
      // expiration check, if now is greater than expires_at
      if (localStorage.getItem('expires_at') * 1000 < new Date().getTime()) {
        console.log('token expired')
        // we need to get a new token before we can get the new data stuffs
        await dispatch('getStravaRefreshedToken', localStorage.getItem('refresh_token'))
      } else {
        await dispatch('getStravaActivities')
      }
    },
    updateExtremes({ commit }, extremes) {
      commit('setExtremes', extremes)
    },
    updateFromLocalStorage({ commit, dispatch }) {
      console.log('Setting state from localstorage (updateFromLocalStorage)')
      commit('setStravaTokens', {
        token_type: localStorage.getItem('token_type'),
        expires_at: localStorage.getItem('expires_at'),
        expires_in: localStorage.getItem('expires_in'),
        refresh_token: localStorage.getItem('refresh_token'),
        access_token: localStorage.getItem('access_token'),
        athlete: JSON.parse(localStorage.getItem('athlete'))
      })
      commit('setActivitiesRaw', JSON.parse(localStorage.getItem('activitiesRaw')))
      commit('setActivitiesOverTime', JSON.parse(localStorage.getItem('activitiesOverTime')))
      commit('setSelectedRide', JSON.parse(localStorage.getItem('activitiesRaw'))[0])
      dispatch('updateLoadingState', false)
    },
    updateLoadingState({ commit }, boolean) {
      commit('setLoadingState', boolean)
    },
    updateLoadingStatus({ commit }, loadingStatus) {
      commit('setLoadingStatus', loadingStatus)
    },
    updateSelectedRide({ commit, state }, rideID) {
      const foundRide = state.activitiesRaw.find(element => element.id === rideID)
      commit('setSelectedRide', foundRide)
    }
  },
  // modules: {}
})
